import * as React from 'react';
import snakeToPascal from '../tools';
import { isFieldVisible } from '../utils/fieldVisibility';
import VariantStyles from "./Draggables/VariantStyles";
import { cn } from '../lib/utils';

// Field Components - Import
import StringField from './ModelDataFields/Input/String';
import FloatField from './ModelDataFields/Input/Float';
import IntField from './ModelDataFields/Input/Int';
import DateField from './ModelDataFields/Input/Date';
import EnumField from './ModelDataFields/Input/Enum';
import BoolField from './ModelDataFields/Input/Bool';
import TimeSerieField from './ModelDataFields/Input/TimeSerie';
import TimeSerieTimingField from './ModelDataFields/Input/TimeSerieTiming';
import RelationField from './ModelDataFields/Input/Relation';
import ListField from './ModelDataFields/Input/List';
import CrossField from './ModelDataFields/Input/CrossField';
import TimingReferenceField from './ModelDataFields/Input/TimingReference';
import CustomizableTimingReferenceField from './ModelDataFields/Input/CustomizableTimingReference';

import OutputFloatField from './ModelDataFields/Output/Float';
import OutputGridField from './ModelDataFields/Output/Grid';
import DynamicGridField from './ModelDataFields/Output/DynamicGrid';

import SensitivityFloatField from './ModelDataFields/Sensitivities/Float';
import SensitivityIntField from './ModelDataFields/Sensitivities/Int';
import SensitivityStringField from './ModelDataFields/Sensitivities/String';
import SensitivityDateField from './ModelDataFields/Sensitivities/Date';
import SensitivityBoolField from './ModelDataFields/Sensitivities/Bool';
import SensitivityCustomizableTimingReferenceField from './ModelDataFields/Sensitivities/CustomizableTimingReference';
import SensitivityTimeSerieField from './ModelDataFields/Sensitivities/TimeSerie';
import SensitivityEnumField from './ModelDataFields/Sensitivities/Enum';
import SensitivityRelationField from './ModelDataFields/Sensitivities/Relation';
import SensitivityTimingReferenceField from './ModelDataFields/Sensitivities/TimingReference';
import SensitivityTimeSerieTimingField from './ModelDataFields/Sensitivities/TimeSerieTiming';
import SensitivityListField from './ModelDataFields/Sensitivities/List';

import OutputTimeSeriesGrid from './ModelDataFields/Output/TimeSeriesGrid';
import InputModelDataValueCommentPopover from './ModelDataFields/Input/reusables/CommentsPopover';
import FieldExplainButton from './ModelDataFields/Input/reusables/FieldExplainButton';
import OutputTimeSeriesGridOperational from './ModelDataFields/Output/TimeSeriesGridOperational';
import TailwindModal from './Tailwind/TailwindModal';
import { useAppStore } from '../shared/stores/useAppStore';
import { useChatOpen, useSharedChat } from '../hooks/useAgentContext';
import ErpBindingButton, { ErpBinding } from './ModelDataFields/Input/reusables/ErpBindingButton';
import FieldBindingModal from './FieldBindingModal';
import apiService from '../services/ApiService';

const SensitivityFieldByType = {
    'str': SensitivityStringField,
    'int': SensitivityIntField,
    'float': SensitivityFloatField,
    'datetime': SensitivityDateField,
    'enum': SensitivityEnumField,
    'bool': SensitivityBoolField,
    'list': SensitivityListField,
    'time_serie_timing': SensitivityTimeSerieTimingField,
    'time_serie': SensitivityTimeSerieField,
    'relation': SensitivityRelationField,
    'timing_reference': SensitivityTimingReferenceField,
    'customizable_timing_reference': SensitivityCustomizableTimingReferenceField,
}

const FieldByKindAndType = {
    'Input': {
        'str': StringField,
        'int': IntField,
        'float': FloatField,
        'datetime': DateField,
        'enum': EnumField,
        'bool': BoolField,
        'list': ListField,
        'time_serie_timing': TimeSerieTimingField,
        'time_serie': TimeSerieField,
        'relation': RelationField,
        'timing_reference': TimingReferenceField,
        'customizable_timing_reference': CustomizableTimingReferenceField,
    },
    'Output': {
        'float': OutputFloatField,
        'grid': OutputGridField,
        'dynamic_grid': DynamicGridField,
    }
}

// Field Description Popover Component (converted to Tailwind)
const FieldDescriptionPopover = ({ field }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <div className="relative inline-block">
            <button
                type="button"
                className="p-1 hover:bg-slate-100 rounded transition-colors -ml-3 mr-1"
                onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                }}
                title="Details"
            >
                <span className="material-symbols-outlined text-[20px] text-slate-400">
                    help_outline
                </span>
            </button>
            {open && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={handleClose}
                    ></div>
                    <div
                        className="fixed z-50 w-64 bg-white rounded-lg shadow-lg border border-slate-200 p-3"
                        style={{
                            top: (anchorEl?.getBoundingClientRect().bottom ?? 0) + 4,
                            left: anchorEl?.getBoundingClientRect().left,
                        }}
                    >
                        {field.description.split('\n').map((str, idx) => (
                            <p key={idx} className="text-sm text-slate-700 mb-1">
                                {str}
                            </p>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// Field types that can be bound to ERP values (simple value types only)
const ERP_BINDABLE_TYPES = ['str', 'int', 'float', 'datetime', 'bool'];

interface ModelDataFieldDetailsPanelProps {
    selectedItem: any | null;
    variant: 'Input' | 'Output';
    currentState: any;
    currentSensitivity: any;
    recreateExcelInput: () => void;
    userRights: any;
    hideExpandButton?: boolean;
    embedded?: boolean;
    scenarioId?: number;
}

export default function ModelDataFieldDetailsPanel({
    selectedItem,
    variant,
    currentState,
    currentSensitivity,
    recreateExcelInput,
    userRights,
    hideExpandButton = false,
    embedded = false,
    scenarioId,
}: ModelDataFieldDetailsPanelProps) {
    const hasErpConnections = useAppStore(s => s.hasErpConnections);

    const [mounted, setMounted] = React.useState(false);
    const [isExpandedOpen, setIsExpandedOpen] = React.useState(false);
    const { setIsChatOpen } = useChatOpen();
    const { sendMessage } = useSharedChat();

    // ERP Binding state
    const [bindingModalOpen, setBindingModalOpen] = React.useState(false);
    const [bindingField, setBindingField] = React.useState<{ name: string; field: any } | null>(null);
    const [bindingRefreshTrigger, setBindingRefreshTrigger] = React.useState(0);
    const [existingBinding, setExistingBinding] = React.useState<ErpBinding | null>(null);

    const handleOpenBindingModal = (name: string, field: any, binding: ErpBinding | null) => {
        setBindingField({ name, field });
        setExistingBinding(binding);
        setBindingModalOpen(true);
    };

    const handleBindingCreated = () => {
        setBindingRefreshTrigger(prev => prev + 1);
    };

    const handleSyncApply = async (field: any, erpValue: any) => {
        let formattedValue = erpValue;

        if (field.type === 'datetime' && erpValue) {
            if (typeof erpValue === 'object' && erpValue.format) {
                formattedValue = erpValue.format('YYYY-MM-DD');
            } else if (typeof erpValue === 'string') {
                formattedValue = erpValue;
            }
        } else if (field.type === 'float' && typeof erpValue === 'string') {
            formattedValue = erpValue.replace(',', '.');
        }

        try {
            const response = await apiService.putModelDataValue(field.id, formattedValue);
            if (response) {
                field.currentValue.value = erpValue;
                currentState.isComputed = false;
                reloadLocalState();
            }
        } catch (error) {
            console.error('Failed to apply ERP value:', error);
        }
    };

    const crossFieldObj = {};

    const reloadLocalState = () => {
        setMounted(!mounted);
    }

    const renderField = (name: string, field: any) => {

        if (selectedItem.sensitivityModelData == null) {
            try {
                const FieldToRender = FieldByKindAndType[selectedItem.kind][field.type];
                field.model_data_id = selectedItem.id;

                if (FieldToRender) {
                    return (
                        <div className="flex flex-col mb-6" key={name}>
                            <div className="flex flex-row items-center mb-2">
                                {field.description ? (
                                    <FieldDescriptionPopover field={field} />
                                ) : (
                                    <div className="w-5 h-5 -ml-3 mr-1" />
                                )}
                                <span className="text-lg font-medium text-slate-900">
                                    {field.display_name ? field.display_name : name}
                                </span>
                                <FieldExplainButton field={field} name={name} />
                                {selectedItem.kind == 'Input' && (
                                    <div className="ml-2">
                                        <InputModelDataValueCommentPopover field={field}/>
                                    </div>
                                )}
                                {hasErpConnections && selectedItem.kind === 'Input' && ERP_BINDABLE_TYPES.includes(field.type) && (
                                    <div className="-mt-1 -ml-1">
                                        <ErpBindingButton
                                            onClick={(binding) => handleOpenBindingModal(name, field, binding)}
                                            fieldId={field.id}
                                            refreshTrigger={bindingRefreshTrigger}
                                            currentValue={field.currentValue?.value}
                                            appFieldType={field.type}
                                            onSyncApply={(erpValue) => handleSyncApply(field, erpValue)}
                                        />
                                    </div>
                                )}
                            </div>
                            <FieldToRender
                                name={name}
                                field={field}
                                siblingFields={selectedItem?.fields ?? {}}
                                reloadState={reloadLocalState}
                                onFieldChange={reloadLocalState}
                                currentState={currentState}
                                recreateExcelInput={recreateExcelInput}
                                userRights={userRights}
                                scenarioId={scenarioId}
                            />
                        </div>
                    );
                }
                else {
                    if (field.type === 'cross_field') {
                        if (crossFieldObj[field.model_data_type]) {
                            crossFieldObj[field.model_data_type].push(field.field_name);
                        } else {
                            crossFieldObj[field.model_data_type] = [field.field_name];
                        }
                    }
                    return null;
                }
            } catch (e) {
                console.log(e);
                return null;
            }
        }
        else {
            try {
                const SensitivityFieldToRender = SensitivityFieldByType[field.type];
                field.model_data_id = selectedItem.id;

                if (SensitivityFieldToRender) {
                    return (
                        <div className="flex flex-col mb-4" key={`${name}-sensitivity`}>
                            <span className="text-lg font-medium text-slate-900 mb-2">
                                {name}
                            </span>
                            <SensitivityFieldToRender
                                name={name}
                                item={selectedItem}
                                field={field}
                                reloadState={reloadLocalState}
                                currentState={currentState}
                                currentSensitivity={currentSensitivity}
                                recreateExcelInput={recreateExcelInput}
                                userRights={userRights}
                            />
                        </div>
                    );
                }
                else {
                    return null;
                }
            } catch (e) {
                console.log(e);
                return null;
            }
        }
    }

    // Empty state when no item selected
    if (!selectedItem) {
        return (
            <div className={cn(
                "flex flex-col gap-4 overflow-y-auto",
                !embedded && "bg-white border border-slate-200 rounded-xl p-6 max-h-[600px]"
            )}>
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest mb-4">
                    Field Details
                </h4>
                <div className="text-xs text-slate-400 italic">
                    Select an item to view and edit its details
                </div>
            </div>
        );
    }

    // Check if item has fields
    const hasFields = Object.entries(selectedItem.fields || {}).filter(([_, field]: any) => isFieldVisible(field, selectedItem.fields)).length > 0;

    const renderFieldsContent = () => (
        <>
            {Object.entries(selectedItem.fields).filter(([_, field]: any) => isFieldVisible(field, selectedItem.fields)).map(([name, field]) => {
                const rendered = renderField(snakeToPascal(name), field);
                if (!rendered) return null;
                return (
                    <div key={`${selectedItem.id}-${name}`} className="border-b border-slate-100 pb-2 last:border-0">
                        {rendered}
                    </div>
                );
            })}
            {currentState['Input'] && (
                <CrossField
                    crossFieldObj={crossFieldObj}
                    reloadState={reloadLocalState}
                    currentState={currentState}
                    recreateExcelInput={recreateExcelInput}
                    userRights={userRights}
                />
            )}
            {selectedItem.kind == 'Output' && currentState['Output'].field_values && (
                <OutputTimeSeriesGrid
                    fields={selectedItem.fields}
                    currentState={currentState}
                />
            )}
            {selectedItem.kind == 'Output' && currentState['Output'].field_values === undefined && (
                <OutputTimeSeriesGridOperational
                    fields={selectedItem.fields}
                />
            )}
        </>
    );

    return (
        <div className={cn(
            "flex flex-col gap-4 overflow-y-auto",
            !embedded && "bg-white border border-slate-200 rounded-xl p-6 max-h-[600px]"
        )}>
            {/* Header */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest">
                        {selectedItem.name} Details
                    </h4>
                    {!hideExpandButton && (
                        <button
                            onClick={() => setIsExpandedOpen(true)}
                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                            title="Expand"
                        >
                            <span className="material-symbols-outlined text-[18px] text-slate-500">open_in_full</span>
                        </button>
                    )}
                </div>

                {/* Item Type Badge + Documentation */}
                <div className="flex items-center gap-2 mb-4">
                    <span
                        className="text-xs font-bold text-white px-2 py-1 rounded"
                        style={{
                            backgroundColor: VariantStyles[variant]["typecolor"]
                        }}
                    >
                        {snakeToPascal(selectedItem.type)}
                    </span>
                    <button
                        className="p-0.5 hover:bg-slate-100 rounded transition-colors flex items-center"
                        onClick={() => {
                            setIsChatOpen(true);
                            sendMessage(`Explain me what is the block named '${selectedItem.name}' (type: ${selectedItem.type}) and how to use it ?`);
                        }}
                        title="More Details"
                    >
                        <span className="material-symbols-outlined text-[18px] text-slate-600">
                            help_outline
                        </span>
                    </button>
                </div>
            </div>

            {/* Fields Section */}
            {hasFields ? (
                <div className="space-y-3">
                    {renderFieldsContent()}
                </div>
            ) : (
                <div className="text-xs text-slate-400 italic">
                    This item has no editable fields
                </div>
            )}

            {/* Expanded Modal */}
            {!hideExpandButton && <TailwindModal
                isOpen={isExpandedOpen}
                onClose={() => setIsExpandedOpen(false)}
                title={`${selectedItem.name} Details`}
                size="full"
            >
                <div className="-mx-6 -my-4 p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
                    {/* Item Type Badge */}
                    <div className="flex items-center gap-2 mb-4">
                        <span
                            className="text-xs font-bold text-white px-2 py-1 rounded"
                            style={{
                                backgroundColor: VariantStyles[variant]["typecolor"]
                            }}
                        >
                            {snakeToPascal(selectedItem.type)}
                        </span>
                    </div>

                    {/* Fields */}
                    {hasFields ? (
                        <div className="space-y-3">
                            {renderFieldsContent()}
                        </div>
                    ) : (
                        <div className="text-xs text-slate-400 italic">
                            This item has no editable fields
                        </div>
                    )}
                </div>
            </TailwindModal>}

            {/* ERP Field Binding Modal */}
            {bindingField && scenarioId && (
                <FieldBindingModal
                    open={bindingModalOpen}
                    setOpen={setBindingModalOpen}
                    fieldName={bindingField.name}
                    field={bindingField.field}
                    modelDataId={selectedItem.id}
                    modelDataName={selectedItem.name}
                    scenarioId={scenarioId}
                    existingBinding={existingBinding}
                    onBindingCreated={handleBindingCreated}
                    currentValue={bindingField.field.currentValue?.value}
                    onSyncApply={(erpValue) => handleSyncApply(bindingField.field, erpValue)}
                />
            )}
        </div>
    );
}
